import { inject, Injectable } from "@angular/core";

import { FgdsProject } from "@domain/project/fgds-project";
import { MonitoringProductListService } from "@domain/project/monitoring-product-list/monitoring-product-list.service";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ProductListService } from "@domain/project/product-list/product-list.service";
import { ProjectCostService } from "@domain/project/product-list/project-cost.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { isFgdsProject } from "@domain/project/project-guards";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { CountryCurrencyMapper } from "@utils/country-currency-mapper";
import { ToastHelper } from "@utils/toast-helper.service";
import Excel from "exceljs";
import { combineLatest, map } from "rxjs";
import { take } from "rxjs/operators";
import { ExportMonitoringProductListBuilder } from "./export-monitoring-product-list.builder";
import { ExportProductListBuilder } from "./export-product-list.builder";

@Injectable({
  providedIn: "root",
})
export class ExportProductListService {
  private projectCostService = inject(ProjectCostService);
  private productListService = inject(ProductListService);
  private userService: UserService = inject(UserService);
  private monitoringProductListService = inject(MonitoringProductListService);
  private countryCurrencyMapper = inject(CountryCurrencyMapper);
  private toastHelper = inject(ToastHelper);
  private readonly fgdsBuilder = new ExportProductListBuilder(
    this.projectCostService,
    this.productListService,
    this.countryCurrencyMapper,
  );
  private readonly monitoringBuilder = new ExportMonitoringProductListBuilder(this.productListService);

  export(project: ScopedProject) {
    if (isFgdsProject(project)) this.exportFgds(project);
    else this.exportMonitoring(project);
  }

  private exportFgds(project: FgdsProject) {
    combineLatest([
      this.productListService.createGroupSectionsForProject(project),
      this.productListService.createAdditionalConfigSections(project),
      this.userService.user$.pipe(take(1)),
    ])
      .pipe(map(([groupSections, additionalSections, user]) => ({ groupSections, additionalSections, user })))
      .subscribe({
        next: (context) => {
          try {
            const workbook = new Excel.Workbook();
            const worksheet = workbook.addWorksheet(this.getWorksheetName(BusinessScope.FGDS));

            this.fgdsBuilder.build(
              worksheet,
              project,
              context.groupSections,
              [],
              context.additionalSections,
              [],
              this.productListService.getLatestPositionIdForProducts(context.groupSections, context.additionalSections),
              context.user.showZrefPrices ?? false,
            );

            void Promise.resolve(this.createAndDownload(workbook, project)).catch((error) =>
              this.handleExportError(project, error),
            );
          } catch (error) {
            this.handleExportError(project, error);
          }
        },
        error: (error) => this.handleExportError(project, error),
      });
  }

  private exportMonitoring(project: MonitoringProject) {
    combineLatest([
      this.createMonitoringGroupSectionsForProject(project),
      this.monitoringProductListService.createAdditionalConfigSections(project),
    ])
      .pipe(
        map(([monitoringGroupSections, additionalMonitoringSections]) => ({
          monitoringGroupSections,
          additionalMonitoringSections,
        })),
      )
      .subscribe({
        next: (context) => {
          try {
            const workbook = new Excel.Workbook();
            const worksheet = workbook.addWorksheet(this.getWorksheetName(BusinessScope.MONITORING));

            const serviceRows = this.monitoringProductListService.createServiceRows(
              project,
              context.monitoringGroupSections,
              context.additionalMonitoringSections,
            );
            this.monitoringBuilder.build(
              worksheet,
              project,
              context.monitoringGroupSections,
              serviceRows,
              context.additionalMonitoringSections,
            );

            void Promise.resolve(this.createAndDownload(workbook, project)).catch((error) =>
              this.handleExportError(project, error),
            );
          } catch (error) {
            this.handleExportError(project, error);
          }
        },
        error: (error) => this.handleExportError(project, error),
      });
  }

  private createMonitoringGroupSectionsForProject(project: MonitoringProject) {
    return this.monitoringProductListService.createGroupSections(project);
  }

  private getWorksheetName(businessScope: BusinessScope) {
    if (businessScope === BusinessScope.MONITORING) {
      return $localize`:@@monitoring.productListExport.worksheetName:Calculation`;
    }

    return $localize`:@@productListExport.worksheetName:Kalkulation`;
  }

  private async createAndDownload(workbook: Excel.Workbook, project: ScopedProject) {
    const data = await workbook.xlsx.writeBuffer();
    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileURL = window.URL.createObjectURL(blob);
    const fileLink = document.createElement("a");
    fileLink.href = fileURL;
    const fileName = `${project.name}.xlsx`;
    fileLink.setAttribute("download", fileName);
    fileLink.setAttribute("target", "_blank");
    document.body.appendChild(fileLink);
    fileLink.click();
    fileLink.remove();
  }

  private handleExportError(project: ScopedProject, error: unknown) {
    console.error("Failed to export product list:", error);
    this.toastHelper.error(
      $localize`:@@toast.productListExportFailed.header:Excel-Export fehlgeschlagen`,
      $localize`:@@toast.productListExportFailed.description:Die Produktliste für ${project.name}:projectName: konnte nicht exportiert werden.`,
    );
  }
}
